import { Component, inject } from '@angular/core';

import { finalize } from 'rxjs';

import { ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { PasswordModule } from 'primeng/password';

// Composants
import { FormBaseComponent } from 'src/app/@shared/components/abstract/form-base.component';
import { MessageAction, MessageComponent } from 'src/app/@shared/components/message/message.component';

// Services
import { AuthenticationService } from 'src/app/@core/authentication/services/authentication.service';

// Utils
import { Logger } from 'src/app/@core/services/logger/logger.service';
import { ODataMessage } from 'src/app/@shared/utilities/odata/message';

const LOGGER = new Logger('SubscribeFormComponent');

@Component({
    selector: 'app-reset-password-form',
    imports: [
    ReactiveFormsModule,
    ButtonModule,
    MessageComponent,
    PasswordModule
],
    templateUrl: './reset-password-form.component.html',
    styleUrls: ['./reset-password-form.component.scss']
})
export class ResetPasswordFormComponent extends FormBaseComponent {
    private _authenticationService = inject(AuthenticationService);

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    public showSuccessMessage: boolean = false;

    /**
     * Motif du refus du lien, tel que le serveur le nomme (#1123) :
     * `RESET_PASSWORD_LINK_EXPIRED`, `RESET_PASSWORD_LINK_INVALID` ou
     * `RESET_PASSWORD_LINK_INCOMPLETE`. Il prend la place du formulaire, parce qu'il n'y a
     * plus rien à ressaisir : c'est le lien qui est refusé, pas le mot de passe. L'écran
     * gardait ses champs jusqu'ici, ce qui invitait à retenter indéfiniment une saisie que
     * personne ne contestait.
     */
    public refusal: ODataMessage | null = null;

    /**
     * Repli quand le refus n'est pas nommé : réponse sans `messages`, erreur serveur ou
     * coupure réseau. Sans lui, ces cas retomberaient sur l'« Erreur inconnue » d'origine.
     */
    private readonly _unknownRefusal: ODataMessage = new ODataMessage({
        domain: 'reset_password',
        longText: `Le renouvellement de votre mot de passe n'a pas abouti. Rouvrez le lien reçu par email dans `
            + `quelques instants, ou demandez-en un nouveau depuis « Mot de passe oublié ? ».`,
        shortText: 'Renouvellement impossible',
        type: 'E'
    });

    /** Sortie commune aux trois refus : la demande d'un lien neuf. */
    public readonly askNewLinkAction: MessageAction = {
        label: `Mot de passe oublié ?`,
        handler: () => this._askNewLink(),
    };

    public readonly loginActions: MessageAction[] = [{
        label: `Revenir à la connexion`,
        handler: () => this._goToLogin(),
    }];

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    public reset(): void {
        this.loading = true;
        this.refusal = null;
        let context = this.form.value;
        context.user_id = this.queryParams.get('user_id');
        context.timestamp = this.queryParams.get('timestamp');
        context.signature = this.queryParams.get('signature');
        const reset$ = this._authenticationService.resetPassword$(context);
        reset$
            .pipe(
                finalize(() => {
                    this.form.markAsPristine();
                    this.loading = false;
                })
            )
            .subscribe({
                next: () => {
                    this._displaySuccess();
                    setTimeout(() => {
                        this.router.navigate(['/login'])
                    }, 5000);
                },
                error: (error: any) => {
                    // Un refus du lien porte un code et aucune erreur de champ ; un mot de
                    // passe refusé, l'inverse. Le serveur ne renvoie jamais les deux, et
                    // vérifie le lien en premier : jamais de bannière ET de champ en rouge.
                    if (this._hasFieldErrors(error)) {
                        Object.keys(error.details).forEach((key) => {
                            let control = this.form.get(key);
                            control?.setErrors({ invalid: true, message: error.details[key].longText });
                        })
                        return;
                    }
                    // Seul un refus **nommé** par le serveur s'affiche tel quel. Le message
                    // générique que le service fabrique quand il n'a rien à dire est
                    // précisément l'« Erreur inconnue » que ce ticket fait disparaître : il
                    // n'a pas de code, et tombe donc sur le repli, qui dit quoi faire.
                    this.refusal = error?.messages?.find((message: ODataMessage) => !!message.code)
                        ?? this._unknownRefusal;
                },
            });
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes protégées
     ----------------------------------------------------------------------------------------*/

    protected override buildForm() {
        this.form = this.formBuilder.group({
            password: ['', Validators.required],
            confirmPassword: ['', Validators.required],
        });
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    private _askNewLink(): void {
        this.router.navigate(['/lost-password']);
    }

    private _displaySuccess(): void {
        this.showSuccessMessage = true;
    }

    private _goToLogin(): void {
        this.router.navigate(['/login']);
    }

    /** Vrai quand le serveur a décoré au moins un champ du formulaire. */
    private _hasFieldErrors(error: any): boolean {
        return Object.keys(error?.details ?? {}).some(key => !!this.form.get(key));
    }

}
